/* eslint-disable */
// ═══════════════════════════════════════════════════════════════════════════════════════
//  GRAIN TARGET ENGINE — the ONE ladder, shared by both tools.
//
//  Kevin, 2026-07-14: "I would like the two target engines to be exactly the same. I want
//  the model farm to operate with those, and I want market intelligence to be something I
//  can use, or even share with farmers."
//
//  Before this, there were two engines that disagreed:
//    Model Farm (api/_grain-wizard.js) — rungs on the 2-yr percentile of what the market
//      has traded at, with a cost-of-production floor under them.
//    Market Intelligence (src/App.jsx) — rungs on measured fill probability, snapped to
//      technical levels.
//  Same farm, same board, two different answers. Now there is one engine and two skins:
//    Model Farm  = the WORKING plan  — bushels per contract month, orders sized to them.
//    Market Intel = the SHAREABLE view — per board, no bushels, safe to hand a farmer.
//
//  CommonJS on purpose: this is the one module both a CRA bundle (import) and a Vercel
//  serverless function (require) can load. Do not convert it to ESM.
//
//  ── WHY COST OF PRODUCTION DOES NOT PLACE A RUNG ──
//  It used to. Model Farm floored every target at breakeven x 1.02, so a below-cost market
//  produced targets that could never fill. Kevin killed it: "just because grain is below
//  cost of production does not always mean you shouldn't be selling. The market does not
//  care about your cost of production. The market doesn't serve you for you to make money.
//  It can be ruthless."
//  So breakeven is CONTEXT — it colors a rung and tells you whether that price pays the
//  bills — but it never moves one. A target is a statement about what the MARKET will do,
//  not about what the farm needs. Those are different questions and conflating them is how
//  you end up holding an unsold crop while waiting for a number that isn't coming.
// ═══════════════════════════════════════════════════════════════════════════════════════

// ── HOW MANY TIMES A LEVEL HAS ACTUALLY HELD — the one thing that made the ladder
// ── "technical in name only", and the only level property it did not have.
//
// SOURCED, not invented. The standard is explicit and quantified across technical
// references: a SINGLE touch is a random event, TWO to THREE is a moderate level, and
// FOUR OR MORE is a strong one — "a level that has produced several clear reactions is
// more significant than an area price touched only once". Grain commentary names levels
// exactly this way: "strong support and resistance at $4.70/bushel, which has been a key
// level multiple times" (DTN on December corn, 2026-07-17).
//
// The ladder ranked candidates by DISTANCE TO AN AIM and nothing else, so a level the
// market had turned at six times and one it had never reached were indistinguishable.
// That is why a round number — of which there is one every 10c or 25c, so one is almost
// always within a cent or two of any aim — won 56-67% of rungs at every horizon.
//
// A TOUCH is an approach that did NOT hold: price came within TOUCH_TOL of the level and
// then turned back down without closing decisively through it. Closing through by more
// than TOUCH_BREAK means the level gave way, and that approach is not a touch — counting
// it would make a level look strongest on the day it failed.
//
// TOUCH_TOL is a FRACTION OF PRICE, not a cent figure, so one definition serves corn at
// $5 and soybeans at $13. Approaches inside one SEPARATION window count once: a level
// tested over five consecutive sessions was tested once, not five times, and counting
// bars would make every slow grind past a level look like a wall.
const TOUCH_TOL = 0.004;      // 0.4% — ~2c on corn, ~5c on beans
const TOUCH_BREAK = 0.006;    // closed through by more than this = the level broke
const TOUCH_SEPARATION = 5;   // sessions; approaches closer together are one test
// ⚠ A TOUCH IS AN APPROACH THAT WAS REJECTED, AND THE REJECTION HAS TO BE CHECKED.
// The first cut tested only the approach BAR — reached the level, did not close far through
// it — which counts a bar that closes at $5.05 on its way to $5.60 as a test the level
// passed. It is the opposite: the level failed there. A touch therefore requires price to
// come back DOWN below the level within TOUCH_CONFIRM sessions. Found by the checker's
// straight-through fixture on its first run, not by reading.
const TOUCH_CONFIRM = 10;     // sessions allowed for the rejection to show
function grainTouchCount(s, lvl) {
  if (!Array.isArray(s) || !s.length || !(lvl > 0)) return 0;
  const tol = lvl * TOUCH_TOL, brk = lvl * TOUCH_BREAK;
  let n = 0, lastAt = -Infinity;
  for (let i = 0; i < s.length; i++) {
    const hi = s[i].h != null ? s[i].h : s[i].c;
    if (hi < lvl - tol) continue;              // never got there
    if (s[i].c > lvl + brk) continue;          // closed decisively through — it broke, not held
    if (i - lastAt < TOUCH_SEPARATION) continue;
    // ⚠ THE APPROACH MUST COME FROM BELOW. A bar on the way DOWN through a level reaches
    // it and is followed by lower closes, so a forward-only rule counts every level a
    // decline passes as "tested" — a falling market would manufacture resistance all the
    // way down. Resistance is tested by a RALLY into it, so there must be a close under
    // the level shortly before, with nothing above it in between.
    let from_below = false;
    for (let j = Math.max(0, i - TOUCH_CONFIRM); j < i; j++) {
      if (s[j].c > lvl + brk) { from_below = false; break; }   // we were already above it
      if (s[j].c < lvl - tol) from_below = true;
    }
    if (!from_below) continue;
    // Did it actually turn away? Look forward for a close back under the level. A level
    // reached at the very end of the series has not yet been rejected, and an approach
    // still in progress is not a test it passed.
    let rejected = false;
    for (let j = i + 1; j <= Math.min(s.length - 1, i + TOUCH_CONFIRM); j++) {
      if (s[j].c < lvl - tol) { rejected = true; break; }
      if (s[j].c > lvl + brk) break;           // broke out instead — not a rejection
    }
    if (!rejected) continue;
    n++; lastAt = i;
  }
  return n;
}

// ── PRIOR SWING HIGHS — the most basic resistance there is, and the ladder had NONE.
//
// The candidate list carried hi1m / hi3m / hi6m / lifeHi, which are four fixed
// LOOKBACKS and not four levels: on a contract that made its high recently they all
// resolve to the SAME BAR. Measured 2026-10-06 over the 15 live grain contracts, the
// raw list averaged 7.5 entries and only 5.7 DISTINCT prices above the market, and
// soybeans offered as few as three (SH27 / SK27 / SN27: a 1-month high and two Fib
// extensions). The ladder needs three rungs in three non-overlapping windows, so it
// fell through to round numbers on 56-67% of rungs at EVERY horizon tested
// (42 / 63 / 126 / 189 / 252 days) — "technical in name only", which is precisely
// what the round-number comment in the ladder predicted would happen.
//
// A swing high is a close that is the highest within ±PIVOT_K sessions of itself: the
// price the market turned at, which is where resting offers actually accumulate. It is
// the one level class a grain marketer would name first and the only one that scales
// with how much history a contract has.
//
// Scoped to the last PIVOT_LOOKBACK sessions — about a year — rather than the whole
// life. That is a stated choice, not a measured one: a turn from two years ago is a
// fact about a different market, and the contract's all-time extreme is already
// carried separately as lifeHi.
const PIVOT_K = 10;
const PIVOT_LOOKBACK = 252;
function grainSwingHighs(s, k = PIVOT_K, lookback = PIVOT_LOOKBACK) {
  const w = (s || []).slice(-lookback);
  const out = [];
  for (let i = k; i < w.length - k; i++) {
    let top = true;
    for (let j = i - k; j <= i + k; j++) { if (w[j].c > w[i].c) { top = false; break; } }
    if (top) out.push({ lvl: w[i].c, on: w[i].d });
  }
  // Collapse a flat top reported on several adjacent bars into one level.
  const uniq = [];
  for (const p of out.sort((a, b) => a.lvl - b.lvl)) {
    if (!uniq.length || p.lvl - uniq[uniq.length - 1].lvl > 1e-9) uniq.push(p);
  }
  return uniq;
}

// ── CONSOLIDATION SHELVES — where the market SPENT ITS TIME.
//
// grainCloseHist was built for the range bar's distribution band and never read as
// LEVELS, which is a real level class the engine was already computing and throwing
// away. A bin holding many days is a price this contract kept returning to, and on
// the way up that is where offers sit.
//
// Only bins that are a LOCAL maximum of the histogram and hold at least
// SHELF_MIN_SHARE of the peak bin qualify — otherwise every bin in a quiet market is
// a "shelf" and the filter has added noise rather than levels. The level is the bin's
// centre; a bin is (hi-lo)/bins wide, about 5c on corn, which is the precision this
// class honestly has.
const SHELF_MIN_SHARE = 0.60;
function grainShelves(hist) {
  if (!hist || !hist.counts || !hist.counts.length) return [];
  const c = hist.counts, n = c.length;
  const w = (hist.hi - hist.lo) / n;
  if (!(w > 0)) return [];
  const peak = Math.max(...c);
  if (!(peak > 0)) return [];
  const out = [];
  for (let i = 0; i < n; i++) {
    if (c[i] < peak * SHELF_MIN_SHARE) continue;
    if (i > 0 && c[i - 1] > c[i]) continue;
    if (i < n - 1 && c[i + 1] > c[i]) continue;
    out.push({ lvl: hist.lo + (i + 0.5) * w, days: c[i] });
  }
  return out;
}

// ── Per-contract technicals, from that contract's OWN daily bars (futures_contract_history).
// This is what a resting order on WN27 should key off — not the nearby's chart.
const GRAIN_MIN_BARS = 30;
// ── WHY A CONTRACT CAN HAVE NO TECHNICALS, SAID OUT LOUD ──
//
// grainContractTech returns null below GRAIN_MIN_BARS and every caller renders NOTHING,
// so the whole market-context section silently disappears. Measured 2026-10-06, that is
// three of the twenty-one live grain contracts: SU27, CH28 and WH28 hold FIFTEEN bars
// each. They are not missing data — CZ27 opened 2026-07-15 and earlier bars do not exist
// anywhere — they are simply new, and a deferred contract is thin until it has traded.
//
// A section that vanishes reads as a broken page. This says which contract, how many
// sessions it has, and how many it needs, so the absence is a fact about the contract
// rather than about Silas.
function grainTechShortfall(rows) {
  const n = (rows || []).filter((r) => r && r.close != null).length;
  if (n >= GRAIN_MIN_BARS) return null;
  return { bars: n, need: GRAIN_MIN_BARS,
    why: n + " session" + (n === 1 ? "" : "s") + " of trading on this contract so far — "
       + GRAIN_MIN_BARS + " are needed before its own technicals mean anything" };
}

function grainContractTech(rows) {
  const s = (rows || []).filter(r => r.close != null)
    .map(r => ({ d: r.bar_date, c: +r.close, h: r.high != null ? +r.high : +r.close }))
    .sort((a, b) => a.d < b.d ? -1 : 1);
  if (s.length < GRAIN_MIN_BARS) return null;
  const closes = s.map(x => x.c), last = closes[closes.length - 1];
  const ma = n => closes.length >= n ? closes.slice(-n).reduce((a, b) => a + b, 0) / n : null;
  const maxH = n => Math.max(...s.slice(Math.max(0, s.length - n)).map(x => x.h));
  const minC = n => Math.min(...s.slice(Math.max(0, s.length - n)).map(x => x.c));
  const chg = []; for (let i = 1; i < closes.length; i++) chg.push(closes[i] - closes[i - 1]);
  const tail = chg.slice(-60);
  const mean = tail.length ? tail.reduce((a, b) => a + b, 0) / tail.length : 0;
  const sigma = tail.length > 5 ? Math.sqrt(tail.reduce((a, b) => a + (b - mean) ** 2, 0) / (tail.length - 1)) : null;
  // Median ABSOLUTE daily move as a fraction of price — the SAME statistic the fill curve
  // measures across history, so the two are comparable. (A stdev vs a median-abs is a ~1.5x
  // apples-to-oranges error that silently widened every rung until it was caught.)
  const absTail = tail.map((c, i) => Math.abs(c) / (closes[closes.length - tail.length + i - 1] || last)).sort((a, b) => a - b);
  const absMove = absTail.length > 5 ? absTail[Math.floor(absTail.length / 2)] : null;
  // RSI(14), Wilder. Reported as CONDITION, never used to place a price — an RSI reading is
  // not a level, and pretending it is would be inventing precision.
  let rsi = null;
  if (chg.length >= 15) {
    let ag = 0, al = 0;
    for (let i = 0; i < 14; i++) { ag += Math.max(0, chg[i]); al += Math.max(0, -chg[i]); }
    ag /= 14; al /= 14;
    for (let i = 14; i < chg.length; i++) { ag = (ag * 13 + Math.max(0, chg[i])) / 14; al = (al * 13 + Math.max(0, -chg[i])) / 14; }
    rsi = al === 0 ? 100 : 100 - 100 / (1 + ag / al);
  }
  // Quantiles of CLOSES, for the range bar's distribution band.
  const qOf = (arr, p) => { if (!arr.length) return null; const a = arr.slice().sort((x, y) => x - y); const i = (a.length - 1) * p, lo = Math.floor(i), hi = Math.ceil(i); return lo === hi ? a[lo] : a[lo] + (a[hi] - a[lo]) * (i - lo); };
  return { last, ma50: ma(50), ma100: ma(100), ma200: ma(200), sigma, absMove, rsi,
    lifeLo: Math.min(...closes), lifeHi: Math.max(...s.map(x => x.h)),
    // lifeHi is the max INTRADAY high — a real level, so the target ladder keeps using it.
    // lifeHiClose is the max CLOSE, and the range bar must use it: that bar's low end and its
    // percentile are both measured on closes, so topping the axis with an intraday spike
    // stretched it (WU26: $7.00 high vs $6.92 max close) and pushed the dot left of where the
    // percentile said price was. One axis, one basis.
    //
    // ⚠ THAT DISTINCTION HAS COLLAPSED ON EVERY LIVE CONTRACT, and the reason is
    // in the FEED, not here. api/_futures.js says it outright — "USDA gives a
    // settle only → close, high/low left null" — so the only bars carrying OHLC
    // are the one-time Yahoo seed and the manual wccChart loads, which stop
    // around 2026-07-09 to 2026-08-21 per contract. Everything after that is
    // close-only and takes the `h: r.high ?? r.close` fallback above.
    //
    // Measured 2026-10-06: lifeHi === lifeHiClose on ALL NINE live contracts
    // (CZ26 CH27 CK27 CN27 CU27 WN27 WU27 SX26 SF27), because each one's life
    // high was set in the Aug–Sep rally, after its last OHLC bar. It still
    // DIFFERS on 19 of 20 expired contracts, so the two fields are not
    // redundant and must not be merged — but the ladder's "contract high" rung
    // is a SETTLE today, not an intraday level, whatever this comment's first
    // line says about the seeded era. Load OHLC before building anything new
    // that needs a true intraday range.
    lifeHiClose: Math.max(...closes),
    q25: qOf(closes, 0.25), q50: qOf(closes, 0.5), q75: qOf(closes, 0.75),
    // The bar's axis is [min(closes), max(closes)] = [lifeLo, lifeHiClose], so the
    // histogram is binned over exactly the span the bar draws. Same array, one axis.
    dist: grainCloseHist(closes),
    // The two level classes the ladder was missing. Computed HERE because the
    // ladder only ever receives `tc` and never the bars.
    pivots: grainSwingHighs(s),
    // The ladder needs the bars to count touches on a level it did not know about
    // when this ran (a round number, a Fib of a swing it picks later). Carried
    // rather than re-derived, so there is one series and one definition of a bar.
    series: s,
    shelves: grainShelves(grainCloseHist(closes)),
    hi1m: maxH(21), hi3m: maxH(63), hi6m: maxH(126), lo3m: minC(63), lo6m: minC(126),
    pctile: closes.filter(x => x <= last).length / closes.length, asOf: s[s.length - 1].d, n: s.length };
}

// ── grainCloseHist — the SHAPE of the closes a range bar is drawn over.
//
// ⚠ A DOT AT A PRICE AND A NUMBER THAT IS A RANK ARE TWO DIFFERENT ANSWERS, and
// the range bar prints them side by side. The dot sits at (last-lo)/(hi-lo) —
// deliberately, because the axis is a PRICE axis and every other mark on it is a
// price — while the text beside it states a percentile of CLOSES. Those only agree
// when closes are evenly spread, and they are not: measured on the live front-month
// wheat series 2026-09-10, today's $7.2875 sits 81.3% of the way up a $4.95-$7.825
// axis and beats 96.4% of the last 252 closes. Kevin presented that to a grower and
// it read as a broken chart, which is the correct reaction to a dot four-fifths along
// a bar labelled 96th.
//
// A quartile BOX was the previous attempt at making the gap legible and it cannot:
// between q75 and the dot the bar drew NOTHING, so the eye had no way to tell whether
// that span held 5% of the days or 50%. Drawing the distribution answers it directly —
// the percentile becomes the shaded fraction of the mass, which is what it is.
//
// Normalised to its OWN tallest bin, because these bars are read one above another and
// a shared vertical scale would flatten a tight distribution into a flat line. Height
// compares bins WITHIN one bar; it never compares two bars.
const GRAIN_HIST_BINS = 28;
const GRAIN_HIST_MIN_N = 20;   // below this a shape is noise wearing the look of a distribution
function grainCloseHist(closes, bins = GRAIN_HIST_BINS) {
  const a = (closes || []).map(Number).filter((x) => Number.isFinite(x));
  if (a.length < GRAIN_HIST_MIN_N) return null;
  const lo = Math.min(...a), hi = Math.max(...a);
  if (!(hi > lo)) return null;              // one price repeated is not a distribution
  const h = new Array(bins).fill(0);
  for (const v of a) h[Math.min(bins - 1, Math.floor((v - lo) / (hi - lo) * bins))]++;
  const peak = Math.max(...h);
  if (!(peak > 0)) return null;
  // lo/hi ride along so a consumer can colour each bin by whether it sits at or below
  // today's close WITHOUT re-deriving the axis and drifting from it.
  return { bins: h.map((c) => c / peak), counts: h, lo, hi, n: a.length };
}

// ── FILL CURVE: how far these markets ACTUALLY rally — measured, not modeled ──
// For every past day: how far did the market get ABOVE that day's price, at its best, over
// the next N trading days? That distribution answers the only question a target really
// poses — "how often does a rung this far up actually get hit?"
//
// It replaced a 1-sigma random-walk guess that was badly wrong in the AGGRESSIVE direction:
// sigma*sqrt(21) called a normal 1-month wheat move +8.7%; the MEASURED median best 1-month
// rally is +2.6%. Daily volatility overstates how far a market travels, because grain chops
// instead of marching.
//
// Three data decisions, each of which moves the answer a LOT:
//  • SOURCE = the continuous front-month series, NOT per-contract history. The contract
//    table holds only 7 contracts over 3 years, and most of its bars are each contract's
//    thin far-out life (corn is UNCHANGED on 17% of those days) — a sample that both
//    understates movement and is far too small to read a percentile off.
//  • ROLL-FREE. The continuous series jumps every time the front month rolls, and in a carry
//    market it jumps UP. Left in, those jumps read as free rallies and inflate every target.
//    The roll lands on the first bar of a month, so that day's return is dropped.
//  • TRAILING 5 YEARS, so "attainable" means attainable in THIS market. Over the full 10
//    years the 2021-22 spike drags wheat's 60%-fill rung from +9.3% to +12.8%.
const GRAIN_FILL_HORIZONS = [21, 42, 63, 126, 189, 252];
function grainFillCurve(contRows) {
  const s = (contRows || []).filter(r => r.close != null && r.bar_date)
    .map(r => ({ d: String(r.bar_date), c: +r.close })).sort((a, b) => a.d < b.d ? -1 : 1).slice(-1260);  // ~5 yrs
  if (s.length < 400) return null;
  const idx = [1], moves = [];                       // roll-free cumulative index
  for (let i = 1; i < s.length; i++) {
    const rolled = s[i].d.slice(0, 7) !== s[i - 1].d.slice(0, 7);   // first bar of a new month = the roll
    const ret = (!rolled && s[i - 1].c > 0) ? s[i].c / s[i - 1].c - 1 : 0;
    idx.push(idx[i - 1] * (1 + ret));
    if (!rolled && s[i - 1].c > 0) moves.push(Math.abs(ret));
  }
  const buckets = {};
  for (const H of GRAIN_FILL_HORIZONS) {
    const g = [];
    for (let i = 0; i + 20 < idx.length; i++) {
      const w = idx.slice(i + 1, i + 1 + H);
      if (w.length < Math.min(20, H)) continue;
      g.push(Math.max(...w) / idx[i] - 1);
    }
    if (g.length >= 150) { g.sort((a, b) => a - b); buckets[H] = g; }
  }
  const have = GRAIN_FILL_HORIZONS.filter(h => buckets[h]);
  if (!have.length) return null;
  const sortedMoves = moves.slice().sort((a, b) => a - b);
  const pick = h => have.reduce((best, x) => Math.abs(x - h) < Math.abs(best - h) ? x : best, have[0]);
  return {
    typicalMove: sortedMoves.length ? sortedMoves[Math.floor(sortedMoves.length / 2)] : null,
    gainForFill: (p, h) => { const g = buckets[pick(h)]; return g[Math.max(0, Math.min(g.length - 1, Math.floor((1 - p) * (g.length - 1))))]; },
    fillForGain: (x, h) => { const g = buckets[pick(h)]; return g.filter(v => v >= x).length / g.length; },
    horizonUsed: h => pick(h),
  };
}

// What the three rungs are FOR (Kevin, 2026-07-14): "the first 2 targets should be realistic
// and attainable based on current market conditions, I'm ok with a flyer 3rd."
// Rung 1 was moved 0.60 → 0.75 on 2026-07-31. Kevin, looking at a CH27 corn ladder with the
// market at $4.87 and rung 1 at $5.20: "that first target is 40 cents away… that's attainable?"
// The 60% number was honest — measured, ~54% of the last five years of days rallied that far
// within six months — but "attainable" has to mean a price this market reaches in the ordinary
// course, not one it reaches slightly more often than not. Three quarters of days is that.
// The cost is that rung 1 now sits close (corn's aim halves, +5.7% → +3.0%), which is the
// point: it's the rung that should actually fill.
const GRAIN_FILL_TARGETS = [
  { p: 0.75, lbl: 'attainable' },   // history cleared this ~75% of the time, in this board's working life
  { p: 0.40, lbl: 'strong' },       // a good price — happens ~40% of the time
  { p: 0.18, lbl: 'flyer' },        // the weather-market order. Rare on purpose.
];

// Trading days a resting order on this board really has to work. The board stops being the
// pricing reference the month BEFORE delivery (the elevator exits ahead of first notice), so
// the clock runs to the START of the delivery month. Capped at ~6 months: nobody leaves an
// order working for a year, and crediting 12 months ran wheat's ladder to $8.75/$9.00 (a long
// window on a sample containing 2022 reads enormous rallies as routine). Re-place next season.
function grainBoardHorizon(contractDate) {
  if (!contractDate) return 126;
  const days = (new Date(String(contractDate).slice(0, 10) + 'T00:00:00') - new Date()) / 86400000;
  return Math.max(15, Math.min(126, Math.round(days * 0.69)));   // calendar days → trading days
}

// ── THE LADDER ──
// 1. AIM at the distance history says gets hit with probability p, within THIS board's
//    remaining life. Near boards therefore get tighter ladders than deferred ones.
// 2. SNAP the aim to the nearest REAL level within ±25%: moving averages, 1/3/6-mo and
//    contract highs, Fibonacci retracements of the last drop, Fib EXTENSIONS above the prior
//    high (so a board at new highs still has levels above it), and round numbers.
// 3. NEAREST-TO-AIM WINS. Confluence (several methods naming one price) is a badge, never a
//    reason to pull a rung further out — that's how an earlier version got unreachable.
// 4. If no real level sits in the zone, say so — the distance itself is the rung, labeled
//    plainly rather than dressed up as a level that isn't there.
// Trend decides WHICH levels are in play (the one piece with a backtest behind it): above the
// 100-day, sell into highs/extensions; below it, sell the bounce, because prior highs above a
// falling market almost never fill — that's how a crop rides unpriced into the harvest low.
function grainTargetLadder(tc, curve = null, horizon = 126) {
  const last = tc.last;
  const trendMA = tc.ma100 != null ? tc.ma100 : tc.ma50;
  const up = trendMA == null ? true : last >= trendMA;
  const offHigh = tc.hi6m ? (tc.hi6m - last) / tc.hi6m : 0;
  const sigma = tc.sigma && tc.sigma > 0 ? tc.sigma : Math.max(0.02, last * 0.012);
  const unit = sigma * Math.sqrt(21);          // a normal ~1-month move — the spacing unit
  // "based on current market conditions" — a quiet market tightens the rungs, a wild one
  // widens them. Both sides are the median absolute daily move, so they're comparable.
  const volAdj = (curve && curve.typicalMove > 0 && tc.absMove > 0)
    ? Math.max(0.7, Math.min(1.4, tc.absMove / curve.typicalMove)) : 1;

  const cand = [];
  const push = (lvl, lbl, round = false) => { if (lvl != null && isFinite(lvl) && lvl > last * 1.002) cand.push({ lvl: +lvl, lbl, round }); };
  push(tc.ma50, '50-day MA'); push(tc.ma100, '100-day MA'); push(tc.ma200, '200-day MA');
  push(tc.hi1m, '1-mo high'); push(tc.hi3m, '3-mo high'); push(tc.hi6m, '6-mo high'); push(tc.lifeHi, 'contract high');
  // ── THE TWO CLASSES THE LADDER WAS MISSING ──
  // Prior swing highs are what grain commentary actually names ("key resistance at
  // $4.92, the market's mid-July high"), and the four lookback highs above are not a
  // substitute: on a contract that topped recently they all resolve to the SAME bar.
  // Measured 2026-10-06, those seven candidates collapsed to as few as THREE distinct
  // prices above the market on soybeans.
  for (const p of (tc.pivots || [])) push(p.lvl, 'prior swing high');
  // A shelf is where this contract SPENT ITS TIME — a price it kept returning to.
  // grainCloseHist already computed the distribution for the range bar; it was never
  // read as levels.
  for (const sh of (tc.shelves || [])) push(sh.lvl, 'held ' + sh.days + ' sessions');
  // ⚠ A RETRACEMENT IS OF A SPECIFIC MOVE, AND THE LABEL HAD TO SAY WHICH.
  //
  // Grain commentary names the move: "$4.70 ... happens to coincide with roughly the 50%
  // retracement level of the May-June break". "79% retrace of the drop" names no drop, so
  // a reader cannot put it on a chart and check it — and an unverifiable level is the
  // thing this whole rebuild is about.
  //
  // The SWING ITSELF is unchanged: the 6-month high and low, which is the window the fill
  // curve and the rest of the ladder already work in. What is added is the two DATES, read
  // back out of the contract's own series, so the label can name the move it retraces.
  // Re-deriving the swing from pivots was considered and not done: the 6-month window is
  // already picking levels the market has tested (WN27's 79% retrace has held twice and
  // sits on the 1-month high), so a second definition would be change without evidence.
  const sHi = tc.hi6m, sLo = tc.lo6m != null ? tc.lo6m : tc.lifeLo;
  // ⚠ AND IT IS NOT ALWAYS A DROP. The label said "retrace of the drop" unconditionally,
  // and dating the swing is what exposed that: WN27 reads its 6-month LOW in June and its
  // HIGH in September, so the move being retraced is a RALLY the market has since pulled
  // back into — the first dated label came out "(Sep–Jun) drop", which is both backwards
  // and the wrong word. The levels are the same arithmetic either way (measured up from the
  // low); what changes is what a reader is told they are looking at.
  //
  // Scoped to the SAME 126 sessions hi6m/lo6m are taken over. Searching the whole series
  // could date the swing to an old bar that merely touched the same price.
  const swingWhen = (() => {
    const ser = (tc.series || []).slice(-126);
    if (!ser.length || sHi == null || sLo == null) return { txt: "", word: "move" };
    const near = (x, y) => Math.abs(x - y) <= Math.max(1e-6, Math.abs(y) * 1e-4);
    let hiAt = -1, loAt = -1;
    for (let i = 0; i < ser.length; i++) {
      const h = ser[i].h != null ? ser[i].h : ser[i].c;
      if (hiAt < 0 && near(h, sHi)) hiAt = i;
      if (loAt < 0 && near(ser[i].c, sLo)) loAt = i;
    }
    if (hiAt < 0 || loAt < 0) return { txt: "", word: "move" };
    const MON = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    // ⚠ A SLICE, NOT A REGEX, AND THAT IS DELIBERATE. The first version of this line was
    // written through a shell and arrived as /^d{4}-(d{2})/ — the backslashes eaten in
    // transit, so it matched a literal "d", never matched a date, and silently dropped the
    // months from every label while the rest of the function worked. That is the
    // backslash-eating trap CLAUDE.md records as having cost a day once already. A bar date
    // is an ISO string by construction here, so positions 5-7 are the month and no escape
    // is needed anywhere.
    const mon = (d) => { const i = +String(d || "").slice(5, 7); return i >= 1 && i <= 12 ? MON[i - 1] : null; };
    const first = Math.min(hiAt, loAt), second = Math.max(hiAt, loAt);
    const A = mon(ser[first].d), B = mon(ser[second].d);
    // High first = the market fell. Low first = it rallied and is pulling back into these.
    const word = hiAt < loAt ? "drop" : "rally";
    return { txt: (A && B && A !== B) ? " " + A + "–" + B : "", word };
  })();
  if (sHi != null && sLo != null && sHi > sLo) {
    const swing = sHi - sLo;
    [[0.382, '38%'], [0.5, '50%'], [0.618, '62%'], [0.786, '79%']].forEach(([f, l]) => push(sLo + f * swing, `${l} retrace of the${swingWhen.txt} ${swingWhen.word}`));
    push(sHi + 0.272 * swing, '1.27 extension'); push(sHi + 0.618 * swing, '1.62 extension');
  }
  // Round numbers are real (the order book clusters there) but the WEAKEST candidate: they sit
  // every 10-25c, so unpenalized they win nearly every snap by proximity and the ladder is
  // "technical" in name only. The handicap lets a true level inside the window beat a nearer
  // round number.
  const rstep = last >= 6 ? 0.25 : 0.10;
  // Two cents. Grain trades in quarter-cent ticks, so this is eight ticks clear of the
  // number — far enough to be in front of the stack, near enough that the level is still
  // the level. It is a stated choice: no source gives a figure, the advice says "a penny
  // or two".
  const ROUND_UNDER = 0.02;
  const ceiling = last * (1 + 1.5 * (curve ? curve.gainForFill(0.10, horizon) : 0.15) * volAdj);
  // ⚠ THE OFFER RESTS JUST UNDER THE ROUND NUMBER, NOT ON IT.
  //
  // Practitioner advice, not a refinement of my own: "avoid round numbers like $5.00 on
  // March corn, instead placing offers a penny or two below round numbers, since
  // technical traders tend to play those same levels and resistance often forms at round
  // numbers." The round number is where the offers STACK UP, which is exactly why resting
  // at it means queueing behind all of them.
  //
  // The level is still IDENTIFIED by its round number — that is what makes it a level —
  // and only the quoted price is stepped down, so the label still names something a
  // reader can find on a chart.
  for (let r = Math.ceil((last + 0.01) / rstep) * rstep; r <= ceiling; r += rstep) {
    const round = +r.toFixed(2);
    const under = +(round - ROUND_UNDER).toFixed(2);
    push(under, 'just under $' + round.toFixed(2), true);
  }
  // ── SIGNIFICANCE OUTRANKS PROXIMITY ──
  //
  // ⚠ THE OLD HANDICAP COULD NOT WORK, AND THE COMMENT ABOVE PREDICTED THE FAILURE IT
  // DID NOT PREVENT. Scoring was `|lvl - aim| * 1.6`, and a round number sitting ON the
  // aim scores ZERO — no multiplier beats zero. They sit every 10c (under $6) or 25c, so
  // one is almost always within a cent or two of any aim. Measured 2026-10-06 over the 15
  // live contracts, round numbers took 56-67% of rungs at EVERY horizon (42/63/126/189/252
  // days); on CZ26 rung one the 50-day MA at $5.07 (score 2.65) lost to round $5.10 (0.11).
  //
  // Round numbers are NOT banned. One the market has actually turned at several times is
  // strong resistance, and practitioners place offers around them deliberately. What is
  // gone is winning on PROXIMITY ALONE.
  //
  // The tiers are the published standard, not a dial: ONE touch is a random event, TWO to
  // THREE is moderate, FOUR OR MORE is strong. A NAMED level (a moving average, a prior
  // high, a Fib of a real swing) gets a floor of tier 1 even untested, because traders
  // watch it whether or not price has reached it. A round number gets NO floor: untested,
  // it is a number rather than a level. Distance to the aim is the TIE-BREAK, not the rank.
  const touchTier = (n) => (n >= 4 ? 3 : n >= 2 ? 2 : n >= 1 ? 1 : 0);
  for (const c of cand) {
    c.touches = grainTouchCount(tc.series || [], c.lvl);
    c.tier = Math.max(touchTier(c.touches), c.round ? 0 : 1);
  }

  const levels = [];
  const fallback = [0.03, 0.05, 0.11];
  for (let i = 0; i < GRAIN_FILL_TARGETS.length; i++) {
    const ft = GRAIN_FILL_TARGETS[i];
    const gain = (curve ? curve.gainForFill(ft.p, horizon) : fallback[i]) * volAdj;
    const aim = last * (1 + gain);
    const win = (aim - last) * 0.25;                        // tight snap window — the fill odds are the point
    const floor = levels.length ? levels[levels.length - 1].lvl + 0.35 * unit : last * 1.002;
    // Strongest evidence first, then nearest to the aim.
    const inWin = cand.filter(c => c.lvl >= floor && Math.abs(c.lvl - aim) <= win)
      .sort((a, b) => (b.tier - a.tier) || (Math.abs(a.lvl - aim) - Math.abs(b.lvl - aim)));
    const pick = inWin.length ? inWin[0] : null;
    const lvl = +(pick ? pick.lvl : Math.max(aim, floor)).toFixed(2);
    // Report the fill odds of the price we ACTUALLY landed on, not of the aim we started from
    // — snapping moves the rung, and the honest number is the one for where it sits.
    const fill = curve ? curve.fillForGain((lvl / last - 1) / (volAdj || 1), horizon) : null;
    const also = pick ? inWin.filter(c => c !== pick && Math.abs(c.lvl - pick.lvl) <= 0.12 * unit).map(c => c.lbl) : [];
    // The rung carries WHY it is a level, not merely that it is one. "prior swing high ·
    // tested 4 times" is checkable against the chart; "round number" is not.
    levels.push({ lvl, lbl: pick ? pick.lbl : 'no level here — distance only', also, measured: !pick,
      kind: ft.lbl, fill, touches: pick ? pick.touches : null, tier: pick ? pick.tier : 0 });
  }
  const mo = Math.max(1, Math.round(horizon / 21));
  return { levels, regime: up ? 'uptrend' : 'downtrend', up, offHigh, trendMA, sigma, rsi: tc.rsi, volAdj,
    horizon, horizonLbl: `~${mo} mo to work` };
}

// ── Which BOARD a delivery month prices off ──
// The elevator quotes basis against contracts like CV26 / CX26 (Oct / Nov corn) that are NOT
// listed CBOT months — those are its own labels. Grain delivered in a non-listed month prices
// off the next listed contract at or after it (Oct corn → Dec board). Without this, Model Farm
// can't find a board's technicals for half its delivery windows.
const GRAIN_LISTED_MONTHS = {
  corn:     [3, 5, 7, 9, 12],
  wheat:    [3, 5, 7, 9, 12],
  soybeans: [1, 3, 5, 7, 8, 9, 11],
};
const GRAIN_MO_CODE = { 1: 'F', 2: 'G', 3: 'H', 4: 'J', 5: 'K', 6: 'M', 7: 'N', 8: 'Q', 9: 'U', 10: 'V', 11: 'X', 12: 'Z' };
const GRAIN_SYM_PFX = { corn: 'C', wheat: 'W', soybeans: 'S' };
function grainBoardSymForMonth(commodity, year, month) {
  const listed = GRAIN_LISTED_MONTHS[commodity]; const pfx = GRAIN_SYM_PFX[commodity];
  if (!listed || !pfx) return null;
  let y = year, m = listed.find(x => x >= month);
  if (m == null) { m = listed[0]; y = year + 1; }            // past the last listed month → roll to next year
  return `${pfx}${GRAIN_MO_CODE[m]}${String(y).slice(2)}`;
}

// ── Breakeven: CONTEXT ONLY. It colors a rung; it never moves one. (See header.) ──
// $/bu = (cash cost per acre − by-product revenue) ÷ expected yield. The by-product credit is
// not optional: wheat throws ~$100/ac of straw, and omitting it overstated wheat's cost by
// $1.11/bu, which made wheat look unsellable at a profit and turned its whole ladder into a
// loss-minimisation exercise.
function grainBreakeven(costPerAcre, yieldPerAcre, byproductPerAcre = 0) {
  const cost = Number(costPerAcre) || 0, y = Number(yieldPerAcre) || 0, bp = Number(byproductPerAcre) || 0;
  const net = cost - bp;
  return (net > 0 && y > 0) ? net / y : null;
}

module.exports = {
  grainContractTech, grainTechShortfall, grainFillCurve, grainTargetLadder, grainBoardHorizon,
  grainBoardSymForMonth, grainBreakeven, grainCloseHist,
  // The level finders. Exported so they can be driven directly: a rule only reachable
  // through the whole ladder can only be tested through whatever the ladder happens to
  // pick, which is how a level class gets "tested" by a fixture that never reaches it.
  grainTouchCount, grainSwingHighs, grainShelves,
  GRAIN_HIST_BINS, GRAIN_HIST_MIN_N,
  GRAIN_FILL_TARGETS, GRAIN_FILL_HORIZONS, GRAIN_LISTED_MONTHS,
};
